import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  ChevronLeft,
  ChevronRight,
  Flag,
  GitBranch,
  Map as MapIcon,
  Plus,
  Trash2,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { apiService } from "../../services/api";
import IterationForecastPanel from "./IterationForecastPanel";
import IterationTargetsCard from "./IterationTargetsCard";
import { buildDefaultSprintPayload } from "../../utils/sprintDefaults";
import { ITERATION_TINTS } from "../../utils/iterationTints";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const MIN_ITERATION_CARD_WIDTH = 220;
const SLOT_SNAP_DISTANCE_PX = 18;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const parseDateInput = (value) => {
  if (!value) return null;
  if (value instanceof Date) return new Date(value);
  if (typeof value === "string") {
    const [year, month, day] = value.split("T")[0].split("-").map(Number);
    if (year && month && day) {
      return new Date(year, month - 1, day);
    }
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const toDate = (value, fallback = new Date()) => {
  if (!value) return new Date(fallback);
  const date = parseDateInput(value);
  if (!date || Number.isNaN(date.getTime())) return new Date(fallback);
  return date;
};

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value) => {
  const date = parseDateInput(value);
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatISO = (date) => {
  const parsed = parseDateInput(date);
  if (!parsed) return "";
  return formatDateInput(parsed);
};

const cloneCalendarDate = (value) => {
  const date = parseDateInput(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const addCalendarDays = (value, days) => {
  const date = cloneCalendarDate(value);
  if (!date) return null;
  date.setDate(date.getDate() + days);
  return date;
};

const diffCalendarDays = (left, right) => {
  const leftDate = cloneCalendarDate(left);
  const rightDate = cloneCalendarDate(right);
  if (!leftDate || !rightDate) return 0;
  return Math.round((leftDate.getTime() - rightDate.getTime()) / DAY_IN_MS);
};

const getFirstMondayInRange = (rangeStart, rangeEnd) => {
  const start = cloneCalendarDate(rangeStart);
  const end = cloneCalendarDate(rangeEnd);
  if (!start || !end) return null;
  const daysToMonday = (8 - start.getDay()) % 7;
  const monday = addCalendarDays(start, daysToMonday);
  return monday && monday <= end ? monday : start;
};

const getLastSaturdayInRange = (rangeStart, rangeEnd) => {
  const start = cloneCalendarDate(rangeStart);
  const end = cloneCalendarDate(rangeEnd);
  if (!start || !end) return null;
  const daysSinceSaturday = (end.getDay() + 1) % 7;
  const saturday = addCalendarDays(end, -daysSinceSaturday);
  return saturday && saturday >= start ? saturday : end;
};

const summarizeObjectives = (objectives = []) => {
  return objectives.reduce(
    (acc, obj) => {
      const status = (obj.status || "backlog").toLowerCase();
      if (status === "done") acc.done += 1;
      else if (status === "in_progress") acc.inProgress += 1;
      else acc.todo += 1;
      return acc;
    },
    { todo: 0, inProgress: 0, done: 0 },
  );
};

const clampBounds = (iterations, slots, milestones = []) => {
  const collectDates = (items) => {
    const dates = [];
    items.forEach((item) => {
      if (item.start_date) dates.push(new Date(item.start_date));
      const end = item.end_date || item.start_date;
      if (end) dates.push(new Date(end));
    });
    return dates;
  };
  const milestoneDates = (milestones || [])
    .map((milestone) => new Date(milestone.milestone_date || milestone.start_date))
    .filter((date) => !Number.isNaN(date.getTime()));

  const iterationDates = collectDates(iterations);
  const slotDates = collectDates(slots);
  const sourceDates = [
    ...(iterationDates.length ? iterationDates : slotDates),
    ...milestoneDates,
  ];

  if (!sourceDates.length) {
    const today = new Date();
    return {
      start: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7,
      ),
      end: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + 60,
      ),
    };
  }
  const min = new Date(Math.min(...sourceDates.map((d) => d.getTime())));
  const max = new Date(Math.max(...sourceDates.map((d) => d.getTime())));
  min.setDate(min.getDate() - 7);
  max.setDate(max.getDate() + 7);
  return { start: min, end: max };
};

const normalizeTimeline = (iterations, slots, bounds) => {
  const totalDuration = Math.max(1, (bounds.end - bounds.start) / DAY_IN_MS);
  const toPercent = (date) =>
    Math.min(
      100,
      Math.max(0, ((date - bounds.start) / DAY_IN_MS / totalDuration) * 100),
    );

  const normalizedSlots = slots
    .filter((slot) => slot.start_date && slot.end_date)
    .map((slot) => {
      const start = toDate(slot.start_date, bounds.start);
      const end = toDate(slot.end_date, start);
      const startMs = start.getTime();
      const endMs = end.getTime();
      if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) {
        return null;
      }
      const boundsStart = bounds.start.getTime();
      const boundsEnd = bounds.end.getTime();
      // Ignore slots that fall completely outside the visible window.
      if (endMs < boundsStart || startMs > boundsEnd) {
        return null;
      }
      const visibleStart = new Date(Math.max(startMs, boundsStart));
      const visibleEnd = new Date(Math.min(endMs, boundsEnd));
      if (visibleEnd <= visibleStart) {
        return null;
      }
      const leftPercent = toPercent(visibleStart);
      const widthDelta = toPercent(visibleEnd) - leftPercent;
      if (widthDelta <= 0) {
        return null;
      }
      return {
        ...slot,
        leftPercent,
        widthPercent: Math.max(1, widthDelta),
      };
    })
    .filter(Boolean);

  const normalizedIterations = iterations.map((iteration, index) => {
    const start = toDate(iteration.start_date, bounds.start);
    const end = toDate(iteration.end_date || iteration.start_date, start);
    const left = toPercent(start);
    const width = Math.max(2, toPercent(end) - left);
    const tint = ITERATION_TINTS[index % ITERATION_TINTS.length];
    const sprints =
      iteration.sprints?.map((sprint, sprintIndex) => {
        const sprintStart = toDate(sprint.start_date, start);
        const sprintEnd = toDate(
          sprint.end_date || sprint.start_date,
          sprintStart,
        );
        const span = Math.max(
          3,
          ((sprintEnd - sprintStart) / (end - start || 1)) * 100,
        );
        const offset = ((sprintStart - start) / (end - start || 1)) * 100;
        return {
          ...sprint,
          key: sprint.id || `s-${sprintIndex}`,
          leftPercent: offset,
          widthPercent: span,
        };
      }) || [];
    return {
      ...iteration,
      leftPercent: left,
      widthPercent: width,
      tint,
      sprints,
      startDateObj: start,
      endDateObj: end,
    };
  });

  return { normalizedSlots, normalizedIterations, totalDuration };
};

const layoutSprintsByOverlap = (sprints = []) => {
  const items = sprints.map((sprint, index) => {
    const start = Number(sprint.leftPercent ?? 0);
    const width = Number(sprint.widthPercent ?? 0);
    const end = start + Math.max(0, width);
    const key = sprint.id ?? sprint.key ?? `sprint-${index}`;
    return { sprint, start, end, key, index };
  });

  const sorted = [...items].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    if (a.end !== b.end) return a.end - b.end;
    return a.index - b.index;
  });

  const laneEnds = [];
  const laneByKey = new Map();

  sorted.forEach((entry) => {
    let lane = 0;
    while (lane < laneEnds.length && entry.start < laneEnds[lane]) {
      lane += 1;
    }
    laneEnds[lane] = entry.end;
    laneByKey.set(entry.key, lane);
  });

  return {
    items: items.map((entry) => ({
      ...entry.sprint,
      lane: laneByKey.get(entry.key) ?? 0,
    })),
    laneCount: Math.max(1, laneEnds.length || 1),
  };
};

const buildTicks = (start, end, width = 640) => {
  if (!start || !end) return [];
  const totalMs = Math.max(1, end.getTime() - start.getTime());
  const approxSegments = Math.max(
    4,
    Math.min(10, Math.round(Math.max(width, 320) / 160) || 6),
  );
  const totalDays = Math.round(totalMs / DAY_IN_MS);
  const stepDays = Math.max(7, Math.round(totalDays / approxSegments) || 7);
  const ticks = [];
  let cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  while (cursor.getTime() <= end.getTime()) {
    ticks.push(new Date(cursor));
    cursor = new Date(cursor);
    cursor.setDate(cursor.getDate() + stepDays);
  }
  const lastTick = ticks[ticks.length - 1];
  if (!lastTick || lastTick.getTime() < end.getTime()) {
    ticks.push(new Date(end));
  }
  return ticks.map((date, index) => ({
    key: `${date.toISOString()}-${index}`,
    label: date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }),
    position: ((date.getTime() - start.getTime()) / totalMs) * 100,
  }));
};

const ProjectTimeline = ({
  project,
  projectId,
  iterations = [],
  slots = [],
  objectives = [],
  attributes = [],
  iterationAttributeTargets = {},
  onAddIterationAttributeTarget,
  onUpdateIterationAttributeTarget,
  onDeleteIterationAttributeTarget,
  onIterationUpdate,
  onIterationInlineUpdate,
  onIterationVisionUpdate,
  onIterationAdd,
  onIterationDelete,
  onMilestoneUpdate,
  onMilestoneSelect,
  onSprintAdd,
  onSprintPatch,
  onSprintDelete,
  onIterationSelect,
  onSprintSelect,
  showProjectHeader = true,
  onViewLevelChange,
}) => {
  const [view, setView] = useState({
    level: "project",
    iterationId: null,
    sprintId: null,
  });
  const [pendingIterations, setPendingIterations] = useState({});
  const [pendingSprints, setPendingSprints] = useState({});
  const [pendingMilestones, setPendingMilestones] = useState({});
  const [dragState, setDragState] = useState(null);
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const iterationRefs = useRef(new Map());
  const [canvasWidth, setCanvasWidth] = useState(0);
  const suppressClickRef = useRef(false);
  const suppressTimeoutRef = useRef(null);
  const iterationCanvasRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const [iterationCanvasWidth, setIterationCanvasWidth] = useState(0);
  const previousUserSelectRef = useRef(null);
  const [iterationHeaderDrafts, setIterationHeaderDrafts] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
  });
  const [iterationHeaderEditing, setIterationHeaderEditing] = useState({
    name: false,
    description: false,
    start_date: false,
    end_date: false,
  });
  const [sprintHeaderDrafts, setSprintHeaderDrafts] = useState({
    description: "",
    vision: "",
    start_date: "",
    end_date: "",
  });
  const [sprintHeaderEditing, setSprintHeaderEditing] = useState({
    description: false,
    vision: false,
    start_date: false,
    end_date: false,
  });
  const [productionData, setProductionData] = useState([]);
  const [productionLoading, setProductionLoading] = useState(false);
  const [productionError, setProductionError] = useState(null);

  useEffect(() => {
    setPendingIterations({});
    setPendingSprints({});
    setPendingMilestones({});
  }, [iterations, slots, objectives]);

  useEffect(() => {
    if (view.level !== "project" || !canvasRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setCanvasWidth(entries[0].contentRect.width);
    });
    observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [view.level]);

  useEffect(() => {
    if (view.level !== "iteration" || !iterationCanvasRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setIterationCanvasWidth(entries[0].contentRect.width);
    });
    observer.observe(iterationCanvasRef.current);
    return () => observer.disconnect();
  }, [view.level]);
  useEffect(() => {
    if (!onViewLevelChange) return;
    onViewLevelChange(view.level);
  }, [view.level, onViewLevelChange]);

  useEffect(
    () => () => {
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
        suppressTimeoutRef.current = null;
      }
    },
    [],
  );

  const displayIterations = useMemo(
    () =>
      iterations.map((it) => {
        const patchedIteration = pendingIterations[it.id]
          ? {
            ...it,
            start_date: pendingIterations[it.id].start_date,
            end_date: pendingIterations[it.id].end_date,
          }
          : it;
        const patchedSprints = (it.sprints || []).map((sprint) =>
          pendingSprints[sprint.id]
            ? {
              ...sprint,
              start_date: pendingSprints[sprint.id].start_date,
              end_date: pendingSprints[sprint.id].end_date,
            }
            : sprint,
        );
        return { ...patchedIteration, sprints: patchedSprints };
      }),
    [iterations, pendingIterations, pendingSprints],
  );

  const milestoneObjectives = useMemo(
    () =>
      (objectives || []).filter(
        (objective) => objective?.is_milestone && objective?.milestone_date,
      ),
    [objectives],
  );

  const displayMilestones = useMemo(
    () =>
      milestoneObjectives.map((milestone) => {
        const pending = pendingMilestones[milestone.id];
        return pending
          ? { ...milestone, milestone_date: pending.milestone_date }
          : milestone;
      }),
    [milestoneObjectives, pendingMilestones],
  );

  const bounds = useMemo(
    () => clampBounds(displayIterations, slots, displayMilestones),
    [displayIterations, slots, displayMilestones],
  );

  const rawActiveIteration = view.iterationId
    ? iterations.find((it) => it.id === view.iterationId) || null
    : null;
  const activeIteration = useMemo(() => {
    if (!rawActiveIteration) return null;
    return (
      displayIterations.find((it) => it.id === rawActiveIteration.id) ||
      rawActiveIteration
    );
  }, [rawActiveIteration, displayIterations]);
  useEffect(() => {
    if (!activeIteration) {
      setIterationHeaderDrafts({
        name: "",
        description: "",
        start_date: "",
        end_date: "",
      });
      setIterationHeaderEditing({
        name: false,
        description: false,
        start_date: false,
        end_date: false,
      });
      return;
    }
    setIterationHeaderDrafts({
      name: activeIteration.name || "",
      description: activeIteration.description || "",
      start_date: activeIteration.start_date || "",
      end_date: activeIteration.end_date || "",
    });
    setIterationHeaderEditing({
      name: false,
      description: false,
      start_date: false,
      end_date: false,
    });
  }, [activeIteration]);

  useEffect(() => {
    if (!projectId) {
      setProductionData([]);
      return;
    }
    let cancelled = false;
    const fetchMetrics = async () => {
      setProductionLoading(true);
      setProductionError(null);
      try {
        const response = await apiService.get(`projects/${projectId}/metrics`);
        if (!cancelled) {
          setProductionData(Array.isArray(response) ? response : []);
        }
      } catch (error) {
        if (!cancelled) {
          setProductionError(
            error?.message || "Impossible de charger les métriques du projet.",
          );
          setProductionData([]);
        }
      } finally {
        if (!cancelled) setProductionLoading(false);
      }
    };
    fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const activeSprint = useMemo(() => {
    if (!view.sprintId) return null;
    if (rawActiveIteration) {
      return (
        (rawActiveIteration.sprints || []).find(
          (s) => s.id === view.sprintId,
        ) || null
      );
    }
    for (const iteration of iterations) {
      const found = (iteration.sprints || []).find(
        (s) => s.id === view.sprintId,
      );
      if (found) return found;
    }
    return null;
  }, [view.sprintId, rawActiveIteration, iterations]);
  useEffect(() => {
    if (!activeSprint) {
      setSprintHeaderDrafts({
        description: "",
        vision: "",
        start_date: "",
        end_date: "",
      });
      setSprintHeaderEditing({
        description: false,
        vision: false,
        start_date: false,
        end_date: false,
      });
      return;
    }
    setSprintHeaderDrafts({
      description: activeSprint.description || "",
      vision: activeSprint.vision || "",
      start_date: activeSprint.start_date || "",
      end_date: activeSprint.end_date || "",
    });
    setSprintHeaderEditing({
      description: false,
      vision: false,
      start_date: false,
      end_date: false,
    });
  }, [activeSprint]);

  useEffect(() => {
    if (view.level === "project") return;
    const iterationExists =
      view.iterationId &&
      iterations.some((iteration) => iteration.id === view.iterationId);
    if (!iterationExists) {
      setView({ level: "project", iterationId: null, sprintId: null });
      return;
    }
    if (view.level === "sprint" && view.sprintId && !activeSprint) {
      setView({
        level: "iteration",
        iterationId: view.iterationId,
        sprintId: null,
      });
    }
  }, [activeSprint, iterations, view.iterationId, view.level, view.sprintId]);

  const orderedIterationsForNav = useMemo(() => {
    return [...displayIterations].sort((a, b) => {
      const dateA =
        parseDateInput(a.start_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const dateB =
        parseDateInput(b.start_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      if (dateA !== dateB) return dateA - dateB;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [displayIterations]);

  const activeIterationSprints = useMemo(() => {
    if (!activeIteration?.sprints?.length) return [];
    return [...activeIteration.sprints].sort((a, b) => {
      const dateA =
        parseDateInput(a.start_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const dateB =
        parseDateInput(b.start_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      if (dateA !== dateB) return dateA - dateB;
      return (a.description || "").localeCompare(b.description || "");
    });
  }, [activeIteration]);

  const activeIterationPosition = useMemo(() => {
    if (!activeIteration) return null;
    const index = orderedIterationsForNav.findIndex(
      (iteration) => iteration.id === activeIteration.id,
    );
    return index >= 0 ? index + 1 : null;
  }, [activeIteration, orderedIterationsForNav]);

  const activeSprintPosition = useMemo(() => {
    if (!activeSprint) return null;
    const index = activeIterationSprints.findIndex(
      (sprint) => sprint.id === activeSprint.id,
    );
    return index >= 0 ? index + 1 : null;
  }, [activeIterationSprints, activeSprint]);

  const projectTimeline = useMemo(
    () => normalizeTimeline(displayIterations, slots, bounds),
    [displayIterations, slots, bounds],
  );
  const slotSnapTargets = useMemo(
    () =>
      (slots || [])
        .map((slot) => {
          const rawStart = cloneCalendarDate(slot.start_date);
          const rawEnd = cloneCalendarDate(slot.end_date || slot.start_date);
          if (!rawStart || !rawEnd) return null;
          const snapStart = getFirstMondayInRange(rawStart, rawEnd);
          const snapEnd = getLastSaturdayInRange(snapStart || rawStart, rawEnd);
          if (!snapStart || !snapEnd || snapEnd < snapStart) return null;
          return {
            id: slot.id,
            name: slot.name,
            start: snapStart,
            end: snapEnd,
          };
        })
        .filter(Boolean),
    [slots],
  );
  const slotSnapEnabled = slotSnapTargets.length > 0;
  const getSnapThresholdDays = useCallback(
    (scale) => {
      if (!slotSnapEnabled || !Number.isFinite(scale) || scale <= 0) return 0;
      return Math.max(2, Math.min(7, Math.round(SLOT_SNAP_DISTANCE_PX / scale)));
    },
    [slotSnapEnabled],
  );
  const findNearestSlotBoundary = useCallback(
    (date, boundary, thresholdDays) => {
      const day = cloneCalendarDate(date);
      if (!day || thresholdDays <= 0) return null;
      return slotSnapTargets.reduce((best, target) => {
        const targetDate = target[boundary];
        const distance = Math.abs(diffCalendarDays(day, targetDate));
        if (distance > thresholdDays) return best;
        if (!best || distance < best.distance) {
          return { target, date: targetDate, distance };
        }
        return best;
      }, null);
    },
    [slotSnapTargets],
  );
  const applySlotSnapToRange = useCallback(
    (rawStart, rawEnd, mode, scale) => {
      if (!slotSnapEnabled) return { start: rawStart, end: rawEnd };
      const thresholdDays = getSnapThresholdDays(scale);
      const durationDays = diffCalendarDays(rawEnd, rawStart);

      if (mode === "resize-start") {
        const startSnap = findNearestSlotBoundary(rawStart, "start", thresholdDays);
        return { start: startSnap ? cloneCalendarDate(startSnap.date) : rawStart, end: rawEnd };
      }
      if (mode === "resize-end") {
        const endSnap = findNearestSlotBoundary(rawEnd, "end", thresholdDays);
        return { start: rawStart, end: endSnap ? cloneCalendarDate(endSnap.date) : rawEnd };
      }

      const startSnap = findNearestSlotBoundary(rawStart, "start", thresholdDays);
      const endSnap = findNearestSlotBoundary(rawEnd, "end", thresholdDays);
      if (startSnap && endSnap && startSnap.target.id === endSnap.target.id) {
        return {
          start: cloneCalendarDate(startSnap.target.start),
          end: cloneCalendarDate(startSnap.target.end),
        };
      }
      if (startSnap) {
        if (Math.abs(diffCalendarDays(rawEnd, startSnap.target.end)) <= thresholdDays) {
          return {
            start: cloneCalendarDate(startSnap.target.start),
            end: cloneCalendarDate(startSnap.target.end),
          };
        }
        return {
          start: cloneCalendarDate(startSnap.target.start),
          end: addCalendarDays(startSnap.target.start, durationDays),
        };
      }
      if (endSnap) {
        return {
          start: addCalendarDays(endSnap.target.end, -durationDays),
          end: cloneCalendarDate(endSnap.target.end),
        };
      }
      return { start: rawStart, end: rawEnd };
    },
    [findNearestSlotBoundary, getSnapThresholdDays, slotSnapEnabled],
  );
  const iterationTintMap = useMemo(() => {
    const map = new Map();
    (projectTimeline.normalizedIterations || []).forEach((iteration) => {
      if (iteration?.id != null) {
        map.set(iteration.id, iteration.tint);
      }
    });
    return map;
  }, [projectTimeline.normalizedIterations]);
  const activeIterationTint =
    activeIteration?.id != null
      ? iterationTintMap.get(activeIteration.id) || null
      : null;
  const iterationFrameClass =
    activeIterationTint?.frame || "border-slate-100 bg-slate-50";
  const iterationPanelClass =
    activeIterationTint?.panel || "border-slate-100 bg-white";

  const minCardWidthPercent = useMemo(() => {
    if (!canvasWidth) return 0;
    return Math.min((MIN_ITERATION_CARD_WIDTH / canvasWidth) * 100, 100);
  }, [canvasWidth]);

  const timelineVisualLayout = useMemo(() => {
    const normalized = projectTimeline.normalizedIterations || [];
    if (!normalized.length) {
      return { iterations: [], laneCount: 1 };
    }

    const computeLayout = (iteration) => {
      const baseLeftPercent = iteration.leftPercent ?? 0;
      const baseWidthPercent = iteration.widthPercent ?? 0;
      const needsCompact =
        minCardWidthPercent > 0 &&
        baseWidthPercent > 0 &&
        baseWidthPercent < minCardWidthPercent;
      const adjustedWidthPercent = needsCompact
        ? Math.max(baseWidthPercent, minCardWidthPercent)
        : baseWidthPercent;
      const maxLeftPercent = Math.max(0, 100 - adjustedWidthPercent);
      const adjustedLeftPercent = needsCompact
        ? clamp(
          baseLeftPercent - (adjustedWidthPercent - baseWidthPercent) / 2,
          0,
          maxLeftPercent,
        )
        : baseLeftPercent;
      const compactHighlight = needsCompact
        ? {
          start: clamp(
            ((baseLeftPercent - adjustedLeftPercent) /
              Math.max(adjustedWidthPercent, 0.0001)) *
            100,
            0,
            100,
          ),
          width: clamp(
            Math.max(
              4,
              (baseWidthPercent / Math.max(adjustedWidthPercent, 0.0001)) *
              100,
            ),
            4,
            100,
          ),
        }
        : null;
      return {
        baseLeftPercent,
        baseWidthPercent,
        needsCompact,
        adjustedLeftPercent,
        adjustedWidthPercent,
        compactHighlight,
      };
    };

    const sorted = normalized
      .map((iteration, index) => {
        const layout = computeLayout(iteration);
        return {
          iteration,
          layout,
          index,
          key: iteration.id ?? `iteration-${index}`,
        };
      })
      .sort((a, b) => {
        const startDiff =
          a.layout.adjustedLeftPercent - b.layout.adjustedLeftPercent;
        if (startDiff !== 0) return startDiff;
        return a.index - b.index;
      });

    const laneEnds = [];
    sorted.forEach((entry) => {
      let lane = 0;
      while (
        lane < laneEnds.length &&
        entry.layout.adjustedLeftPercent < laneEnds[lane]
      ) {
        lane += 1;
      }
      laneEnds[lane] =
        entry.layout.adjustedLeftPercent + entry.layout.adjustedWidthPercent;
      entry.lane = lane;
    });

    const placementByKey = new Map(
      sorted.map((entry) => [
        entry.key,
        { layout: entry.layout, lane: entry.lane },
      ]),
    );

    const orderedIterations = normalized.map((iteration, index) => {
      const key = iteration.id ?? `iteration-${index}`;
      const placement = placementByKey.get(key) || {
        layout: computeLayout(iteration),
        lane: 0,
      };
      return {
        iteration,
        key,
        ...placement,
      };
    });

    return {
      iterations: orderedIterations,
      laneCount: Math.max(1, laneEnds.length || 1),
    };
  }, [projectTimeline, minCardWidthPercent]);

  const timelineTicks = useMemo(
    () => buildTicks(bounds.start, bounds.end, canvasWidth),
    [bounds, canvasWidth],
  );

  const timelineMilestones = useMemo(() => {
    const duration = Math.max(1, bounds.end.getTime() - bounds.start.getTime());
    return displayMilestones
      .map((milestone, index) => {
        const date = parseDateInput(milestone.milestone_date);
        if (!date) return null;
        const position = clamp(
          ((date.getTime() - bounds.start.getTime()) / duration) * 100,
          0,
          100,
        );
        return {
          ...milestone,
          key: milestone.id || `milestone-${index}`,
          leftPercent: position,
          date,
        };
      })
      .filter(Boolean);
  }, [bounds, displayMilestones]);

  const todayPosition = useMemo(() => {
    const today = new Date();
    if (today < bounds.start || today > bounds.end) return null;
    const total = Math.max(1, bounds.end.getTime() - bounds.start.getTime());
    return ((today.getTime() - bounds.start.getTime()) / total) * 100;
  }, [bounds]);
  const iterationBounds = useMemo(() => {
    if (!activeIteration) return null;
    const start = toDate(activeIteration.start_date, new Date());
    const end = toDate(
      activeIteration.end_date || activeIteration.start_date,
      start,
    );
    return { start, end };
  }, [activeIteration]);

  const todayTimestamp = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime();
  }, []);
  const iterationTodayPosition = useMemo(() => {
    if (!iterationBounds) return null;
    const today = new Date();
    if (today < iterationBounds.start || today > iterationBounds.end)
      return null;
    const total = Math.max(
      1,
      iterationBounds.end.getTime() - iterationBounds.start.getTime(),
    );
    return ((today.getTime() - iterationBounds.start.getTime()) / total) * 100;
  }, [iterationBounds]);

  const iterationTimeline = useMemo(() => {
    if (!activeIteration || !iterationBounds) return null;
    return normalizeTimeline([activeIteration], [], iterationBounds);
  }, [activeIteration, iterationBounds]);

  const iterationTicks = useMemo(
    () =>
      iterationBounds
        ? buildTicks(
          iterationBounds.start,
          iterationBounds.end,
          iterationCanvasWidth || canvasWidth,
        )
        : [],
    [iterationBounds, iterationCanvasWidth, canvasWidth],
  );

  const iterationSlotsOverlay = useMemo(() => {
    if (!iterationBounds) return [];
    if (!slots?.length) return [];
    return normalizeTimeline([], slots, iterationBounds).normalizedSlots.filter(
      (slot) => {
        const slotStart = toDate(slot.start_date, iterationBounds.start);
        const slotEnd = toDate(slot.end_date || slot.start_date, slotStart);
        return (
          slotEnd >= iterationBounds.start && slotStart <= iterationBounds.end
        );
      },
    );
  }, [iterationBounds, slots]);

  const objectivesBySprint = useMemo(() => {
    const map = new Map();
    objectives.forEach((obj) => {
      if (!obj?.sprint_id) return;
      if (!map.has(obj.sprint_id)) map.set(obj.sprint_id, []);
      map.get(obj.sprint_id).push(obj);
    });
    return map;
  }, [objectives]);

  const sprintProgressMap = useMemo(() => {
    const map = new Map();
    objectivesBySprint.forEach((items, sprintId) => {
      const summary = summarizeObjectives(items);
      const total = summary.todo + summary.inProgress + summary.done;
      if (total > 0) {
        map.set(sprintId, summary.done / total);
      }
    });
    return map;
  }, [objectivesBySprint]);

  const pixelsPerDay = canvasWidth / Math.max(1, projectTimeline.totalDuration);
  const iterationPixelsPerDay = iterationTimeline
    ? (iterationCanvasWidth || canvasWidth || 1) /
    Math.max(1, iterationTimeline.totalDuration)
    : pixelsPerDay;
  const preparedProduction = useMemo(() => {
    let accHours = 0;
    let accTokens = 0;
    return (productionData || [])
      .map((entry) => {
        const dateObj = toDate(
          entry.date || entry.day || entry.timestamp,
          new Date(),
        );
        const hoursDaily = Number(entry.hours_daily ?? entry.hours ?? 0) || 0;
        const tokensDaily =
          Number(entry.tokens_daily ?? entry.tokens ?? 0) || 0;
        accHours += hoursDaily;
        accTokens += tokensDaily;
        return {
          timestamp: dateObj.getTime(),
          dateLabel: formatISO(dateObj),
          hours_cum:
            typeof entry.hours_cum === "number" ? entry.hours_cum : accHours,
          tokens_cum:
            typeof entry.tokens_cum === "number" ? entry.tokens_cum : accTokens,
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [productionData]);
  const effectiveRange = useMemo(() => {
    if (view.level === "project" || !iterationBounds) return bounds;
    return iterationBounds;
  }, [view.level, bounds, iterationBounds]);
  const productionChartDomain = useMemo(() => {
    if (!effectiveRange) return null;
    return [effectiveRange.start.getTime(), effectiveRange.end.getTime()];
  }, [effectiveRange]);
  const productionRangeLabel = useMemo(() => {
    if (!effectiveRange) return "";
    const options = { day: "2-digit", month: "short" };
    return `${effectiveRange.start.toLocaleDateString("fr-FR", options)} → ${effectiveRange.end.toLocaleDateString("fr-FR", options)}`;
  }, [effectiveRange]);
  const filteredProduction = useMemo(() => {
    if (!productionChartDomain || !preparedProduction.length) return [];
    const [startTs, endTs] = productionChartDomain;
    const inRange = preparedProduction.filter(
      (point) => point.timestamp >= startTs && point.timestamp <= endTs,
    );
    const findLastBefore = (ts) => {
      for (let i = preparedProduction.length - 1; i >= 0; i -= 1) {
        if (preparedProduction[i].timestamp <= ts) return preparedProduction[i];
      }
      return null;
    };
    const createPoint = (ts, base) => ({
      timestamp: ts,
      dateLabel: formatISO(new Date(ts)),
      hours_cum: base?.hours_cum ?? 0,
      tokens_cum: base?.tokens_cum ?? 0,
    });
    const enriched = [...inRange];
    const startBase = findLastBefore(startTs);
    if (!enriched.length || enriched[0].timestamp > startTs) {
      enriched.unshift(createPoint(startTs, startBase));
    }
    const lastPoint = enriched[enriched.length - 1];
    const endBase = findLastBefore(endTs) || lastPoint;
    if (!lastPoint || lastPoint.timestamp < endTs) {
      enriched.push(createPoint(endTs, endBase));
    }
    return enriched.map((point) => ({
      ...point,
      dateLabel: point.dateLabel || formatISO(new Date(point.timestamp)),
    }));
  }, [preparedProduction, productionChartDomain]);
  const toggleIterationHeaderField = useCallback((field, isEditing) => {
    setIterationHeaderEditing((prev) => ({ ...prev, [field]: isEditing }));
  }, []);
  const handleIterationHeaderDraftChange = useCallback((field, value) => {
    setIterationHeaderDrafts((prev) => ({ ...prev, [field]: value }));
  }, []);
  const getIterationFieldValue = useCallback(
    (field) => {
      if (!activeIteration) return "";
      if (field === "name") return activeIteration.name || "";
      if (field === "description") return activeIteration.description || "";
      if (field === "start_date") return activeIteration.start_date || "";
      if (field === "end_date") return activeIteration.end_date || "";
      return "";
    },
    [activeIteration],
  );
  const cancelIterationHeaderField = useCallback(
    (field) => {
      const fallback = getIterationFieldValue(field);
      setIterationHeaderDrafts((prev) => ({ ...prev, [field]: fallback }));
      toggleIterationHeaderField(field, false);
    },
    [getIterationFieldValue, toggleIterationHeaderField],
  );
  const commitIterationHeaderField = useCallback(
    (field) => {
      if (!activeIteration) return;
      const rawValue = iterationHeaderDrafts[field] ?? "";
      const trimmedValue =
        typeof rawValue === "string" ? rawValue.trim() : rawValue;
      const currentValue = getIterationFieldValue(field);
      const closeField = () => toggleIterationHeaderField(field, false);

      if (field === "name") {
        const nextValue = trimmedValue || currentValue;
        setIterationHeaderDrafts((prev) => ({ ...prev, name: nextValue }));
        closeField();
        if (nextValue && nextValue !== currentValue) {
          onIterationInlineUpdate?.(activeIteration.id, "name", nextValue);
        }
        return;
      }
      if (field === "description") {
        setIterationHeaderDrafts((prev) => ({
          ...prev,
          description: trimmedValue,
        }));
        closeField();
        if (trimmedValue !== currentValue) {
          if (onIterationVisionUpdate)
            onIterationVisionUpdate(activeIteration.id, trimmedValue);
          else
            onIterationInlineUpdate?.(
              activeIteration.id,
              "description",
              trimmedValue,
            );
        }
        return;
      }
      if (!rawValue) {
        setIterationHeaderDrafts((prev) => ({
          ...prev,
          [field]: currentValue,
        }));
        closeField();
        return;
      }
      setIterationHeaderDrafts((prev) => ({ ...prev, [field]: rawValue }));
      closeField();
      if (rawValue !== currentValue) {
        onIterationInlineUpdate?.(activeIteration.id, field, rawValue);
      }
    },
    [
      activeIteration,
      getIterationFieldValue,
      iterationHeaderDrafts,
      onIterationInlineUpdate,
      onIterationVisionUpdate,
      toggleIterationHeaderField,
    ],
  );
  const handleIterationHeaderKeyDown = useCallback(
    (field, event) => {
      if (
        event.key === "Enter" &&
        (field !== "description" || !event.shiftKey)
      ) {
        event.preventDefault();
        commitIterationHeaderField(field);
      } else if (event.key === "Escape") {
        event.preventDefault();
        cancelIterationHeaderField(field);
      }
    },
    [cancelIterationHeaderField, commitIterationHeaderField],
  );
  const toggleSprintHeaderField = useCallback((field, isEditing) => {
    setSprintHeaderEditing((prev) => ({ ...prev, [field]: isEditing }));
  }, []);
  const handleSprintHeaderDraftChange = useCallback((field, value) => {
    setSprintHeaderDrafts((prev) => ({ ...prev, [field]: value }));
  }, []);
  const getSprintFieldValue = useCallback(
    (field) => {
      if (!activeSprint) return "";
      if (field === "description") return activeSprint.description || "";
      if (field === "vision") return activeSprint.vision || "";
      if (field === "start_date") return activeSprint.start_date || "";
      if (field === "end_date") return activeSprint.end_date || "";
      return "";
    },
    [activeSprint],
  );
  const cancelSprintHeaderField = useCallback(
    (field) => {
      const fallback = getSprintFieldValue(field);
      setSprintHeaderDrafts((prev) => ({ ...prev, [field]: fallback }));
      toggleSprintHeaderField(field, false);
    },
    [getSprintFieldValue, toggleSprintHeaderField],
  );
  const commitSprintHeaderField = useCallback(
    (field) => {
      if (!activeSprint) return;
      const rawValue = sprintHeaderDrafts[field] ?? "";
      const trimmedValue =
        typeof rawValue === "string" ? rawValue.trim() : rawValue;
      const currentValue = getSprintFieldValue(field);
      const closeField = () => toggleSprintHeaderField(field, false);

      if (field === "description" || field === "vision") {
        const nextValue =
          field === "description" ? trimmedValue || currentValue : trimmedValue;
        setSprintHeaderDrafts((prev) => ({ ...prev, [field]: nextValue }));
        closeField();
        if (nextValue !== currentValue) {
          onSprintPatch?.(activeSprint.id, { [field]: nextValue });
        }
        return;
      }
      if (!rawValue) {
        setSprintHeaderDrafts((prev) => ({ ...prev, [field]: currentValue }));
        closeField();
        return;
      }
      setSprintHeaderDrafts((prev) => ({ ...prev, [field]: rawValue }));
      closeField();
      if (rawValue !== currentValue) {
        onSprintPatch?.(activeSprint.id, { [field]: rawValue });
      }
    },
    [
      activeSprint,
      getSprintFieldValue,
      onSprintPatch,
      sprintHeaderDrafts,
      toggleSprintHeaderField,
    ],
  );
  const handleSprintHeaderKeyDown = useCallback(
    (field, event) => {
      if (event.key === "Enter" && (field !== "vision" || !event.shiftKey)) {
        event.preventDefault();
        commitSprintHeaderField(field);
      } else if (event.key === "Escape") {
        event.preventDefault();
        cancelSprintHeaderField(field);
      }
    },
    [cancelSprintHeaderField, commitSprintHeaderField],
  );
  const formatChartTick = useCallback((value) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
    });
  }, []);

  const consumeClickSuppression = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    if (suppressTimeoutRef.current) {
      clearTimeout(suppressTimeoutRef.current);
      suppressTimeoutRef.current = null;
    }
    return true;
  }, []);

  const markDragMovement = useCallback(() => {
    if (suppressTimeoutRef.current) {
      clearTimeout(suppressTimeoutRef.current);
      suppressTimeoutRef.current = null;
    }
    suppressClickRef.current = true;
  }, []);

  const restoreUserSelect = useCallback(() => {
    if (!document?.body) return;
    if (previousUserSelectRef.current === null) return;
    document.body.style.userSelect = previousUserSelectRef.current;
    previousUserSelectRef.current = null;
  }, []);

  useEffect(() => {
    if (!dragState) return;
    const handlePointerMove = (event) => {
      const deltaPx = event.clientX - dragState.startX;
      const scale = dragState.pixelsPerDay || pixelsPerDay;
      const deltaDays = deltaPx / Math.max(1, scale);
      if (Math.abs(deltaDays) < 0.02) return;
      markDragMovement();
      if (dragState.entity === "iteration") {
        const start = toDate(dragState.originalStart);
        const end = toDate(dragState.originalEnd);
        let newStart = new Date(start.getTime());
        let newEnd = new Date(end.getTime());
        const deltaMs = deltaDays * DAY_IN_MS;
        if (dragState.mode === "move") {
          newStart = new Date(start.getTime() + deltaMs);
          newEnd = new Date(end.getTime() + deltaMs);
        } else if (dragState.mode === "resize-start") {
          newStart = new Date(start.getTime() + deltaMs);
        } else if (dragState.mode === "resize-end") {
          newEnd = new Date(end.getTime() + deltaMs);
        }
        const snappedRange = applySlotSnapToRange(
          newStart,
          newEnd,
          dragState.mode,
          scale,
        );
        newStart = snappedRange.start;
        newEnd = snappedRange.end;
        if (newStart > newEnd) {
          if (dragState.mode === "resize-start") newStart = newEnd;
          else newEnd = newStart;
        }
        setPendingIterations((prev) => ({
          ...prev,
          [dragState.id]: {
            start_date: formatISO(newStart),
            end_date: formatISO(newEnd),
          },
        }));
      } else if (dragState.entity === "milestone") {
        const start = toDate(dragState.originalStart);
        const deltaMs = deltaDays * DAY_IN_MS;
        const nextDate = new Date(start.getTime() + deltaMs);
        setPendingMilestones((prev) => ({
          ...prev,
          [dragState.id]: {
            milestone_date: formatISO(nextDate),
          },
        }));
      } else if (dragState.entity === "sprint") {
        const start = toDate(dragState.originalStart);
        const end = toDate(dragState.originalEnd);
        let newStart = new Date(start.getTime());
        let newEnd = new Date(end.getTime());
        const deltaMs = deltaDays * DAY_IN_MS;
        if (dragState.mode === "move") {
          newStart = new Date(start.getTime() + deltaMs);
          newEnd = new Date(end.getTime() + deltaMs);
        } else if (dragState.mode === "resize-start") {
          newStart = new Date(start.getTime() + deltaMs);
        } else if (dragState.mode === "resize-end") {
          newEnd = new Date(end.getTime() + deltaMs);
        }
        const snappedRange = applySlotSnapToRange(
          newStart,
          newEnd,
          dragState.mode,
          scale,
        );
        newStart = snappedRange.start;
        newEnd = snappedRange.end;
        if (newStart > newEnd) {
          if (dragState.mode === "resize-start") newStart = newEnd;
          else newEnd = newStart;
        }
        setPendingSprints((prev) => ({
          ...prev,
          [dragState.id]: {
            start_date: formatISO(newStart),
            end_date: formatISO(newEnd),
          },
        }));
      }
    };

    const handlePointerUp = () => {
      // Clear suppression after a short delay to ensure the click event (which fires after pointerup)
      // sees the suppression flag if we were dragging.
      if (suppressClickRef.current) {
        suppressTimeoutRef.current = setTimeout(() => {
          suppressClickRef.current = false;
          suppressTimeoutRef.current = null;
        }, 100);
      }
      restoreUserSelect();

      if (dragState.entity === "iteration") {
        const latest = pendingIterations[dragState.id];
        if (latest) {
          onIterationUpdate?.(dragState.id, {
            start_date: latest.start_date,
            end_date: latest.end_date,
          });
        }
      } else if (dragState.entity === "milestone") {
        const latest = pendingMilestones[dragState.id];
        if (latest) {
          onMilestoneUpdate?.(dragState.id, {
            milestone_date: latest.milestone_date,
          });
        }
      } else if (dragState.entity === "sprint") {
        const latest = pendingSprints[dragState.id];
        if (latest) {
          onSprintPatch?.(dragState.id, {
            start_date: latest.start_date,
            end_date: latest.end_date,
          });
        }
      }
      setDragState(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      restoreUserSelect();
    };
  }, [
    dragState,
    pixelsPerDay,
    onIterationUpdate,
    onMilestoneUpdate,
    onSprintPatch,
    pendingIterations,
    pendingMilestones,
    pendingSprints,
    markDragMovement,
    restoreUserSelect,
    applySlotSnapToRange,
  ]);

  const beginDrag = useCallback(
    (config) => (event) => {
      const {
        stopPropagation = false,
        preventDefault = true,
        ...payload
      } = config;
      if (stopPropagation) event.stopPropagation();
      if (preventDefault) event.preventDefault();
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
        suppressTimeoutRef.current = null;
      }
      suppressClickRef.current = false;
      if (document?.body && previousUserSelectRef.current === null) {
        previousUserSelectRef.current = document.body.style.userSelect || "";
        document.body.style.userSelect = "none";
      }
      setDragState({
        ...payload,
        startX: event.clientX,
        originalStart: payload.startDate,
        originalEnd: payload.endDate,
        pixelsPerDay: payload.pixelsPerDay || pixelsPerDay,
      });
    },
    [pixelsPerDay],
  );

  const handleIterationZoom = useCallback(
    (iteration) => {
      setView({
        level: "iteration",
        iterationId: iteration.id,
        sprintId: null,
      });
      onIterationSelect?.(iteration, { skipFocus: true });
    },
    [onIterationSelect],
  );

  const handleSprintZoom = useCallback(
    (iterationId, sprint) => {
      setView({ level: "sprint", iterationId, sprintId: sprint.id });
      onSprintSelect?.(sprint, { skipFocus: true });
    },
    [onSprintSelect],
  );

  const handleIterationCardClick = useCallback(
    (iteration) => {
      if (!iteration) return;
      if (consumeClickSuppression()) return;
      handleIterationZoom(iteration);
    },
    [handleIterationZoom, consumeClickSuppression],
  );

  const handleTimelineSprintClick = useCallback(
    (iterationId, sprint) => {
      if (!sprint) return;
      if (consumeClickSuppression()) return;
      handleSprintZoom(iterationId, sprint);
    },
    [handleSprintZoom, consumeClickSuppression],
  );

  const handleAddSprint = useCallback(
    (iteration) => {
      if (!iteration?.id) return;
      const payload = buildDefaultSprintPayload(iteration);
      onSprintAdd?.(iteration.id, { iteration_id: iteration.id, ...payload });
    },
    [onSprintAdd],
  );

  const handleTimelineSprintDelete = useCallback(
    (event, sprint) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (!onSprintDelete || !sprint?.id) return;
      const iterationId = activeIteration?.id || sprint.iteration_id;
      if (!iterationId) return;
      if (window.confirm("Supprimer ce sprint ?")) {
        onSprintDelete(sprint.id, iterationId);
      }
    },
    [onSprintDelete, activeIteration],
  );

  const navigateToProjectLevel = useCallback(() => {
    setView({ level: "project", iterationId: null, sprintId: null });
  }, []);

  const navigateToIterationLevel = useCallback(
    (iterationId = activeIteration?.id) => {
      if (!iterationId) {
        navigateToProjectLevel();
        return;
      }
      const iteration = displayIterations.find(
        (item) => String(item.id) === String(iterationId),
      );
      if (!iteration) {
        navigateToProjectLevel();
        return;
      }
      handleIterationZoom(iteration);
    },
    [
      activeIteration,
      displayIterations,
      handleIterationZoom,
      navigateToProjectLevel,
    ],
  );

  const navigateToSprintLevel = useCallback(
    (sprintId) => {
      if (!activeIteration?.id || !sprintId) return;
      const sprint = activeIterationSprints.find(
        (item) => String(item.id) === String(sprintId),
      );
      if (!sprint) return;
      handleSprintZoom(activeIteration.id, sprint);
    },
    [activeIteration, activeIterationSprints, handleSprintZoom],
  );

  const navigateUpOneLevel = useCallback(() => {
    if (view.level === "sprint") {
      setView({
        level: "iteration",
        iterationId: activeIteration?.id || view.iterationId,
        sprintId: null,
      });
      return;
    }
    if (view.level === "iteration") {
      navigateToProjectLevel();
    }
  }, [activeIteration, navigateToProjectLevel, view.iterationId, view.level]);

  const renderLevelNavigation = () => {
    const currentLevelLabel =
      view.level === "sprint"
        ? "Niveau sprint"
        : view.level === "iteration"
          ? "Niveau itération"
          : "Niveau roadmap";
    const upLabel =
      view.level === "sprint" ? "Retour itération" : "Retour roadmap";

    return (
      <div className="sticky top-2 z-30 rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-sm backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
              {view.level === "sprint" ? (
                <Flag className="h-3.5 w-3.5" />
              ) : view.level === "iteration" ? (
                <GitBranch className="h-3.5 w-3.5" />
              ) : (
                <MapIcon className="h-3.5 w-3.5" />
              )}
              {currentLevelLabel}
            </span>
            {project?.name && (
              <>
                <span className="hidden h-5 w-px bg-slate-200 sm:block" />
                <span className="hidden max-w-[220px] truncate text-sm font-semibold text-slate-900 sm:inline">
                  {project.name}
                </span>
              </>
            )}
            <nav
              aria-label="Navigation roadmap"
              className="flex min-w-0 flex-wrap items-center gap-1 text-sm"
            >
              <button
                type="button"
                onClick={navigateToProjectLevel}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium transition ${
                  view.level === "project"
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <MapIcon className="h-3.5 w-3.5" />
                Roadmap
              </button>
              {activeIteration && (
                <>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                  <button
                    type="button"
                    onClick={() => navigateToIterationLevel(activeIteration.id)}
                    className={`inline-flex max-w-[240px] items-center gap-1 rounded-full px-2 py-1 font-medium transition ${
                      view.level === "iteration"
                        ? "bg-blue-600 text-white"
                        : "text-slate-500 hover:bg-blue-50 hover:text-blue-700"
                    }`}
                    title={
                      activeIteration.name || `Itération ${activeIteration.id}`
                    }
                  >
                    <GitBranch className="h-3.5 w-3.5 flex-none" />
                    <span className="truncate">
                      {activeIterationPosition
                        ? `${activeIterationPosition}. `
                        : ""}
                      {activeIteration.name ||
                        `Itération ${activeIteration.id}`}
                    </span>
                  </button>
                </>
              )}
              {view.level === "sprint" && activeSprint && (
                <>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                  <span
                    className="inline-flex max-w-[220px] items-center gap-1 rounded-full bg-indigo-600 px-2 py-1 font-medium text-white"
                    title={activeSprint.description || "Sprint"}
                  >
                    <Flag className="h-3.5 w-3.5 flex-none" />
                    <span className="truncate">
                      {activeSprintPosition ? `${activeSprintPosition}. ` : ""}
                      {activeSprint.description || "Sprint"}
                    </span>
                  </span>
                </>
              )}
            </nav>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={activeIteration?.id ?? ""}
              onChange={(event) => navigateToIterationLevel(event.target.value)}
              disabled={!orderedIterationsForNav.length}
              className="max-w-[220px] rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
              aria-label="Aller à une itération"
            >
              <option value="">Itération</option>
              {orderedIterationsForNav.map((iteration, index) => (
                <option key={iteration.id} value={iteration.id}>
                  {index + 1}. {iteration.name || `Itération ${iteration.id}`}
                </option>
              ))}
            </select>
            <select
              value={view.level === "sprint" ? activeSprint?.id ?? "" : ""}
              onChange={(event) => navigateToSprintLevel(event.target.value)}
              disabled={!activeIteration || !activeIterationSprints.length}
              className="max-w-[220px] rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
              aria-label="Aller à un sprint"
            >
              <option value="">Sprint</option>
              {activeIterationSprints.map((sprint, index) => (
                <option key={sprint.id} value={sprint.id}>
                  {index + 1}. {sprint.description || `Sprint ${sprint.id}`}
                </option>
              ))}
            </select>
            {view.level !== "project" && (
              <button
                type="button"
                onClick={navigateUpOneLevel}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                <ChevronLeft className="h-4 w-4" />
                {upLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBreadcrumbs = (options = {}) => {
    const { className = "" } = options;
    if (view.level === "project") return null;
    const items = [{ label: "Roadmap", level: "project" }];
    if (activeIteration) {
      items.push({
        label: activeIteration.name || `Itération ${activeIteration.id}`,
        level: "iteration",
      });
    }
    if (view.level === "sprint" && activeSprint) {
      items.push({
        label: activeSprint.description || "Sprint",
        level: "sprint",
      });
    }
    const baseClassName =
      "flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500";
    const containerClassName = className
      ? `${baseClassName} ${className}`.trim()
      : `mb-4 ${baseClassName}`;
    return (
      <div className={containerClassName}>
        {items.map((item, index) => (
          <React.Fragment key={item.level}>
            {index > 0 && <ChevronRight className="h-3 w-3 text-slate-400" />}
            <button
              onClick={() => {
                if (item.level === "project")
                  setView({
                    level: "project",
                    iterationId: null,
                    sprintId: null,
                  });
                else if (item.level === "iteration")
                  setView({
                    level: "iteration",
                    iterationId: activeIteration?.id || null,
                    sprintId: null,
                  });
              }}
              className={`transition ${index === items.length - 1 ? "text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            >
              {item.label}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderProductionChart = () => {
    if (!projectId || !productionChartDomain) return null;
    const contextLabel =
      view.level === "project" || !iterationBounds ? "Projet" : "Itération";
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">
              Production ({contextLabel.toLowerCase()})
            </p>
            <p className="text-sm text-slate-600">Tokens & heures cumulés</p>
          </div>
          <span className="text-xs font-semibold text-slate-400">
            {productionRangeLabel}
          </span>
        </div>
        <div className="mt-3 h-48">
          {productionLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-500">
              Chargement des métriques…
            </div>
          ) : productionError ? (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
              {productionError}
            </div>
          ) : filteredProduction.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-500">
              Aucune donnée de production disponible sur cette période.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={filteredProduction}
                margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  type="number"
                  dataKey="timestamp"
                  domain={productionChartDomain}
                  tickFormatter={formatChartTick}
                  minTickGap={32}
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(value) => `${Math.round(value)}`}
                  stroke="#10b981"
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(value) => `${Math.round(value)}`}
                  stroke="#9333ea"
                />
                <Tooltip
                  labelFormatter={(value) =>
                    new Date(value).toLocaleDateString("fr-FR")
                  }
                  formatter={(value, name) => [`${Math.round(value)}`, name]}
                />
                <Legend />
                {productionChartDomain &&
                  todayTimestamp >= productionChartDomain[0] &&
                  todayTimestamp <= productionChartDomain[1] && (
                    <ReferenceLine
                      x={todayTimestamp}
                      stroke="#ef4444"
                      strokeDasharray="4 4"
                      yAxisId="left"
                      label={{
                        value: `Aujourd'hui · ${new Date(
                          todayTimestamp,
                        ).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "short",
                        })}`,
                        position: "top",
                        fill: "#ef4444",
                        fontSize: 11,
                        dy: -8,
                      }}
                    />
                  )}
                <Line
                  type="monotone"
                  dataKey="hours_cum"
                  name="Heures cumulées"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={false}
                  yAxisId="left"
                />
                <Line
                  type="monotone"
                  dataKey="tokens_cum"
                  name="Tokens cumulés"
                  stroke="#9333ea"
                  strokeWidth={2}
                  dot={false}
                  yAxisId="right"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  const renderIterationHeader = () => {
    if (view.level === "project" || !activeIteration) return null;
    const metrics = [
      {
        label: "Budget temps",
        value: `${activeIteration.budget_time_hours || 0} h`,
        caption: `Consommé ${activeIteration.consumed_time_hours || 0} h`,
      },
      {
        label: "Budget achats",
        value: `${activeIteration.budget_purchase_eur || 0} €`,
        caption: `Consommé ${activeIteration.consumed_purchase_eur || 0} €`,
      },
      {
        label: "CA",
        value: `${activeIteration.valeur || 0} €`,
        caption: activeIteration.is_ca_forecast_overridden
          ? "CA réel"
          : "CA prévisionnel",
      },
    ];
    return (
      <div className={`mb-3 rounded-xl border p-3 ${iterationFrameClass}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {renderBreadcrumbs({ className: "mb-0" })}
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
            {iterationHeaderEditing.start_date ? (
              <input
                type="date"
                lang="fr"
                autoFocus
                value={iterationHeaderDrafts.start_date || ""}
                onChange={(event) =>
                  handleIterationHeaderDraftChange(
                    "start_date",
                    event.target.value,
                  )
                }
                onBlur={() => commitIterationHeaderField("start_date")}
                onKeyDown={(event) =>
                  handleIterationHeaderKeyDown("start_date", event)
                }
                className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : (
              <button
                type="button"
                onClick={() => toggleIterationHeaderField("start_date", true)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-400 hover:text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
              >
                {formatDisplayDate(iterationHeaderDrafts.start_date) ||
                  "Date de début"}
              </button>
            )}
            <span className="text-slate-400">→</span>
            {iterationHeaderEditing.end_date ? (
              <input
                type="date"
                lang="fr"
                autoFocus
                value={iterationHeaderDrafts.end_date || ""}
                onChange={(event) =>
                  handleIterationHeaderDraftChange(
                    "end_date",
                    event.target.value,
                  )
                }
                onBlur={() => commitIterationHeaderField("end_date")}
                onKeyDown={(event) =>
                  handleIterationHeaderKeyDown("end_date", event)
                }
                className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : (
              <button
                type="button"
                onClick={() => toggleIterationHeaderField("end_date", true)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-400 hover:text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
              >
                {formatDisplayDate(iterationHeaderDrafts.end_date) ||
                  "Date de fin"}
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-[200px]">
                {iterationHeaderEditing.name ? (
                  <input
                    autoFocus
                    value={iterationHeaderDrafts.name}
                    onChange={(event) =>
                      handleIterationHeaderDraftChange(
                        "name",
                        event.target.value,
                      )
                    }
                    onBlur={() => commitIterationHeaderField("name")}
                    onKeyDown={(event) =>
                      handleIterationHeaderKeyDown("name", event)
                    }
                    className="w-full rounded-full border border-slate-300 px-3 py-1.5 text-base font-semibold text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleIterationHeaderField("name", true)}
                    className="text-left text-lg font-semibold text-slate-900 hover:text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                  >
                    {iterationHeaderDrafts.name ||
                      `Itération ${activeIteration.id}`}
                  </button>
                )}
              </div>
              <span className="hidden h-6 w-px rounded bg-slate-200 sm:block" />
              <div className="flex flex-1 flex-wrap items-start gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Vision
                </span>
                <div className="flex-1 min-w-[220px]">
                  {iterationHeaderEditing.description ? (
                    <textarea
                      autoFocus
                      rows={2}
                      value={iterationHeaderDrafts.description}
                      onChange={(event) =>
                        handleIterationHeaderDraftChange(
                          "description",
                          event.target.value,
                        )
                      }
                      onBlur={() => commitIterationHeaderField("description")}
                      onKeyDown={(event) =>
                        handleIterationHeaderKeyDown("description", event)
                      }
                      className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Décrire rapidement la vision de cette itération"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        toggleIterationHeaderField("description", true)
                      }
                      className="line-clamp-2 text-left text-sm text-slate-600 hover:text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                      title="Modifier la vision de cette itération"
                    >
                      {iterationHeaderDrafts.description?.trim() ||
                        "Définir la vision de cette itération"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setView({ level: "project", iterationId: null, sprintId: null });
            }}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white shadow-sm transition hover:bg-slate-800"
          >
            <ChevronLeft className="h-3 w-3" />
            Retour roadmap
          </button>
        </div>
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="rounded-lg border border-white/60 bg-white px-3 py-2 text-sm"
            >
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {metric.label}
              </p>
              <p className="text-lg font-semibold text-slate-900">
                {metric.value}
              </p>
              <p className="text-xs text-slate-500">{metric.caption}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSprintHeader = () => {
    if (view.level !== "sprint" || !activeSprint) return null;
    const sprintObjectives = objectivesBySprint.get(activeSprint.id) || [];
    const summary = summarizeObjectives(sprintObjectives);
    return (
      <div className="mb-4 rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {renderBreadcrumbs({ className: "mb-0" })}
          <div className="flex flex-wrap items-center gap-2 text-sm text-indigo-700">
            {sprintHeaderEditing.start_date ? (
              <input
                type="date"
                lang="fr"
                autoFocus
                value={sprintHeaderDrafts.start_date || ""}
                onChange={(event) =>
                  handleSprintHeaderDraftChange(
                    "start_date",
                    event.target.value,
                  )
                }
                onBlur={() => commitSprintHeaderField("start_date")}
                onKeyDown={(event) =>
                  handleSprintHeaderKeyDown("start_date", event)
                }
                className="rounded-full border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            ) : (
              <button
                type="button"
                onClick={() => toggleSprintHeaderField("start_date", true)}
                className="rounded-full border border-indigo-100 bg-white px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:border-indigo-400 hover:text-indigo-900 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
              >
                {formatDisplayDate(sprintHeaderDrafts.start_date) ||
                  "Date de debut"}
              </button>
            )}
            <span className="text-indigo-300">-></span>
            {sprintHeaderEditing.end_date ? (
              <input
                type="date"
                lang="fr"
                autoFocus
                value={sprintHeaderDrafts.end_date || ""}
                onChange={(event) =>
                  handleSprintHeaderDraftChange(
                    "end_date",
                    event.target.value,
                  )
                }
                onBlur={() => commitSprintHeaderField("end_date")}
                onKeyDown={(event) =>
                  handleSprintHeaderKeyDown("end_date", event)
                }
                className="rounded-full border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            ) : (
              <button
                type="button"
                onClick={() => toggleSprintHeaderField("end_date", true)}
                className="rounded-full border border-indigo-100 bg-white px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:border-indigo-400 hover:text-indigo-900 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
              >
                {formatDisplayDate(sprintHeaderDrafts.end_date) ||
                  "Date de fin"}
              </button>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <p className="text-xs uppercase tracking-wide text-indigo-600">
              Sprint
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-[200px]">
                {sprintHeaderEditing.description ? (
                  <input
                    autoFocus
                    value={sprintHeaderDrafts.description}
                    onChange={(event) =>
                      handleSprintHeaderDraftChange(
                        "description",
                        event.target.value,
                      )
                    }
                    onBlur={() => commitSprintHeaderField("description")}
                    onKeyDown={(event) =>
                      handleSprintHeaderKeyDown("description", event)
                    }
                    className="w-full rounded-full border border-indigo-200 px-3 py-1.5 text-base font-semibold text-indigo-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      toggleSprintHeaderField("description", true)
                    }
                    className="text-left text-xl font-semibold text-indigo-900 hover:text-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                  >
                    {sprintHeaderDrafts.description || "Sprint"}
                  </button>
                )}
              </div>
              <span className="hidden h-6 w-px rounded bg-indigo-200 sm:block" />
              <div className="flex flex-1 flex-wrap items-start gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-400">
                  Vision
                </span>
                <div className="flex-1 min-w-[240px]">
                  {sprintHeaderEditing.vision ? (
                    <textarea
                      autoFocus
                      rows={2}
                      value={sprintHeaderDrafts.vision}
                      onChange={(event) =>
                        handleSprintHeaderDraftChange(
                          "vision",
                          event.target.value,
                        )
                      }
                      onBlur={() => commitSprintHeaderField("vision")}
                      onKeyDown={(event) =>
                        handleSprintHeaderKeyDown("vision", event)
                      }
                      className="w-full rounded-2xl border border-indigo-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="Decrire rapidement la vision de ce sprint"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleSprintHeaderField("vision", true)}
                      className="line-clamp-2 text-left text-sm text-slate-600 hover:text-slate-900 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                      title="Modifier la vision de ce sprint"
                    >
                      {sprintHeaderDrafts.vision?.trim() ||
                        "Definir la vision de ce sprint"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setView({
                level: "iteration",
                iterationId: view.iterationId,
                sprintId: null,
              });
            }}
            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-700 hover:border-indigo-300 hover:text-indigo-900"
          >
            <ChevronLeft className="h-3 w-3" />
            Retour iteration
          </button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-indigo-100 bg-white p-3 text-sm">
            <p className="text-xs uppercase tracking-wide text-indigo-500">
              Objectifs a faire
            </p>
            <p className="text-xl font-semibold text-indigo-900">
              {summary.todo}
            </p>
          </div>
          <div className="rounded-xl border border-indigo-100 bg-white p-3 text-sm">
            <p className="text-xs uppercase tracking-wide text-indigo-500">
              Objectifs en cours
            </p>
            <p className="text-xl font-semibold text-indigo-900">
              {summary.inProgress}
            </p>
          </div>
          <div className="rounded-xl border border-indigo-100 bg-white p-3 text-sm">
            <p className="text-xs uppercase tracking-wide text-indigo-500">
              Objectifs termines
            </p>
            <p className="text-xl font-semibold text-indigo-900">
              {summary.done}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderProjectView = () => {
    const laneHeight = 140;
    const laneCount = timelineVisualLayout.laneCount;
    const containerHeight = laneCount * laneHeight + 32;
    const timelineIterations = timelineVisualLayout.iterations;

    const timelineCanvas = (
      <div
        ref={timelineContainerRef}
        className="rounded-xl border border-slate-100 bg-gradient-to-b from-slate-50 via-white to-slate-50 p-4 shadow-inner"
      >
        <div className="relative w-full" ref={canvasRef}>
          {projectTimeline.normalizedSlots.length > 0 && (
            <div className="relative h-7 rounded-full bg-white/80 shadow-inner">
              {projectTimeline.normalizedSlots.map((slot) => (
                <div
                  key={slot.id || slot.name}
                  className="absolute top-0 h-full rounded-full bg-purple-500/20 text-center text-[11px] font-semibold text-purple-800"
                  style={{
                    left: `${slot.leftPercent}%`,
                    width: `${slot.widthPercent}%`,
                  }}
                >
                  <span className="px-2 leading-7">
                    {slot.name || `Slot ${slot.id}`}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="relative mt-6 h-8">
            {timelineTicks.map((tick) => (
              <div
                key={tick.key}
                className="absolute flex -translate-x-1/2 flex-col items-center text-[11px] font-semibold uppercase tracking-wide text-slate-400"
                style={{ left: `${tick.position}%` }}
              >
                <span>{tick.label}</span>
                <span className="mt-1 h-3 w-px bg-slate-200" />
              </div>
            ))}
          </div>
          <div className="relative mt-4" style={{ height: containerHeight }}>
            {timelineTicks.map((tick) => (
              <div
                key={`grid-${tick.key}`}
                className="pointer-events-none absolute inset-y-0 w-px bg-slate-100"
                style={{ left: `${tick.position}%` }}
              />
            ))}
            {todayPosition !== null && (
              <div
                className="pointer-events-none absolute inset-y-0 w-px bg-emerald-400/80 z-20"
                style={{ left: `${todayPosition}%` }}
              >
                <span className="absolute -top-6 -translate-x-1/2 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-emerald-600 shadow">
                  Aujourd'hui
                </span>
                <span className="absolute -bottom-2 -translate-x-1/2 rounded-full bg-emerald-400 p-1" />
              </div>
            )}
            {timelineMilestones.map((milestone, index) => {
              const markerTop = 8 + (index % Math.max(1, laneCount)) * 28;
              return (
                <div key={milestone.key}>
                  <div
                    className="pointer-events-none absolute inset-y-0 w-px border-l border-dashed border-rose-300"
                    style={{ left: `${milestone.leftPercent}%`, zIndex: 18 }}
                  />
                  <button
                    type="button"
                    className="absolute flex max-w-[190px] -translate-x-1/2 cursor-grab items-center gap-1.5 rounded-full border border-rose-200 bg-white px-2 py-1 text-left text-[11px] font-semibold text-slate-800 shadow-sm transition hover:border-rose-300 hover:bg-rose-50"
                    style={{
                      left: `${milestone.leftPercent}%`,
                      top: `${markerTop}px`,
                      zIndex: 35,
                    }}
                    title={`${milestone.name || "Jalon"} - ${formatDisplayDate(
                      milestone.milestone_date,
                    )}`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (consumeClickSuppression()) return;
                      onMilestoneSelect?.(milestone);
                    }}
                    onPointerDown={beginDrag({
                      entity: "milestone",
                      mode: "move",
                      id: milestone.id,
                      startDate: milestone.milestone_date,
                      endDate: milestone.milestone_date,
                      preventDefault: false,
                    })}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                      <Flag className="h-3.5 w-3.5" />
                    </span>
                    <span className="truncate">{milestone.name || "Jalon"}</span>
                  </button>
                </div>
              );
            })}
            {timelineIterations.length === 0 && timelineMilestones.length === 0 && (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                Aucune iteration planifiee.
              </div>
            )}
            {timelineIterations.map(
              ({ iteration, layout, lane, key }, index) => {
                const {
                  adjustedLeftPercent,
                  adjustedWidthPercent,
                  needsCompact,
                  compactHighlight,
                } = layout;
                const descriptionClampClass = needsCompact
                  ? "line-clamp-2"
                  : "line-clamp-1";
                const iterationTint =
                  iteration?.tint ||
                  ITERATION_TINTS[index % ITERATION_TINTS.length];

                return (
                  <div
                    key={key ?? iteration.id ?? index}
                    className={`group absolute flex min-h-[110px] cursor-pointer flex-col rounded-2xl border p-4 pr-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl ${iterationTint.card}`}
                    style={{
                      left: `${adjustedLeftPercent}%`,
                      width: `${adjustedWidthPercent}%`,
                      top: `${lane * laneHeight}px`,
                    }}
                    ref={(el) => {
                      if (el) iterationRefs.current.set(iteration.id, el);
                      else iterationRefs.current.delete(iteration.id);
                    }}
                    onClick={() => handleIterationCardClick(iteration)}
                    onPointerDown={beginDrag({
                      entity: "iteration",
                      mode: "move",
                      id: iteration.id,
                      startDate: iteration.start_date,
                      endDate: iteration.end_date,
                      preventDefault: false,
                    })}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {iteration.name || `Iteration ${iteration.id}`}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {formatDisplayDate(iteration.start_date) || "..."} ->{" "}
                          {formatDisplayDate(iteration.end_date) || "..."}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleAddSprint(iteration);
                          }}
                          onPointerDown={(event) => event.stopPropagation()}
                          className="rounded-full p-1 text-blue-600 hover:bg-blue-50"
                          title="Ajouter un sprint"
                          aria-label="Ajouter un sprint"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            if (
                              window.confirm(
                                "Supprimer cette itération et ses sprints ?",
                              )
                            ) {
                              onIterationDelete?.(iteration.id);
                            }
                          }}
                          className="rounded-full p-1 text-rose-500 hover:bg-rose-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {needsCompact && compactHighlight && (
                      <div className="relative mt-2 mb-1 h-1 rounded-full bg-slate-200/80">
                        <div
                          className={`absolute top-0 h-full rounded-full ${iterationTint.highlight}`}
                          style={{
                            left: `${compactHighlight.start}%`,
                            width: `${compactHighlight.width}%`,
                          }}
                        />
                      </div>
                    )}
                    <p
                      className={`mt-2 text-xs text-slate-500 ${descriptionClampClass}`}
                    >
                      {iteration.description || "Vision à définir"}
                    </p>
                    {(() => {
                      const sprintLayout = layoutSprintsByOverlap(
                        iteration.sprints || [],
                      );
                      const sprintRowHeight = 18;
                      const sprintRowGap = 4;
                      const sprintTopOffset = 2;
                      const sprintLaneHeight = sprintRowHeight + sprintRowGap;
                      const sprintContainerHeight = Math.max(
                        24,
                        sprintLayout.laneCount * sprintLaneHeight -
                          sprintRowGap +
                          sprintTopOffset * 2,
                      );

                      return (
                        <div
                          className="relative mt-3 rounded-2xl bg-slate-100/70 px-1"
                          style={{ height: sprintContainerHeight }}
                        >
                          {sprintLayout.items.length > 0 ? (
                            sprintLayout.items.map((sprint) => {
                              const completion =
                                sprintProgressMap.get(sprint.id) ?? null;
                              const completionPercent =
                                completion === null
                                  ? null
                                  : Math.min(
                                    100,
                                    Math.max(0, Math.round(completion * 100)),
                                  );
                              return (
                                <div
                                  key={sprint.key}
                                  className="group/sprint absolute rounded-full border border-blue-500/20 bg-white/80 px-2 py-1 text-[10px] text-blue-900 shadow-sm"
                                  style={{
                                    left: `${sprint.leftPercent}%`,
                                    width: `${sprint.widthPercent}%`,
                                    top: `${sprintTopOffset +
                                      sprint.lane * sprintLaneHeight}px`,
                                  }}
                                  title={`${sprint.description || "Sprint"} ${completionPercent !== null
                                      ? `- ${completionPercent}%`
                                      : ""
                                    }`}
                                  aria-label={`Sprint ${sprint.description || ""}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleTimelineSprintClick(
                                      iteration.id,
                                      sprint,
                                    );
                                  }}
                                >
                              <div
                                className="w-full cursor-grab"
                                onPointerDown={
                                  sprint.id
                                    ? beginDrag({
                                        entity: "sprint",
                                        mode: "move",
                                        id: sprint.id,
                                        startDate: sprint.start_date,
                                        endDate: sprint.end_date,
                                        stopPropagation: true,
                                      })
                                    : undefined
                                }
                              >
                                <div className="relative h-4 w-full rounded-full bg-blue-100/70">
                                  {completionPercent !== null ? (
                                    <div
                                      className="absolute inset-y-0 left-0 rounded-full bg-blue-500"
                                      style={{
                                        width: `${completionPercent}%`,
                                      }}
                                    />
                                  ) : (
                                    <div className="absolute inset-0 rounded-full bg-blue-200/60" />
                                  )}
                                  <span className="absolute inset-0 flex items-center justify-center px-1 text-[9px] font-semibold text-blue-900/90">
                                    <span className="truncate rounded-full bg-white/85 px-1.5 py-0.5">
                                      {sprint.description || "Sprint"}
                                    </span>
                                  </span>
                                </div>
                              </div>
                              <div className="pointer-events-none absolute left-1/2 top-full z-20 hidden -translate-x-1/2 pt-2 group-hover/sprint:block">
                                <div className="max-w-[200px] rounded-lg border border-blue-100 bg-white px-2 py-1 text-[10px] text-slate-700 shadow-lg">
                                  {sprint.vision?.trim() || "Vision a definir"}
                                </div>
                              </div>
                                  {sprint.id && (
                                    <>
                                      <div
                                        className="absolute inset-y-0 left-0 w-1 cursor-ew-resize"
                                        onPointerDown={beginDrag({
                                          entity: "sprint",
                                          mode: "resize-start",
                                          id: sprint.id,
                                          startDate: sprint.start_date,
                                          endDate: sprint.end_date,
                                          stopPropagation: true,
                                        })}
                                      />
                                      <div
                                        className="absolute inset-y-0 right-0 w-1 cursor-ew-resize"
                                        onPointerDown={beginDrag({
                                          entity: "sprint",
                                          mode: "resize-end",
                                          id: sprint.id,
                                          startDate: sprint.start_date,
                                          endDate: sprint.end_date,
                                          stopPropagation: true,
                                        })}
                                      />
                                    </>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="absolute inset-1 rounded-full border border-dashed border-slate-200 text-[10px] text-slate-400">
                              <button
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleAddSprint(iteration);
                                }}
                                className="flex h-full w-full items-center justify-center gap-1 text-slate-400 hover:text-slate-600"
                              >
                                <Plus className="h-3 w-3" />
                                Ajouter sprint
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {iteration.id && (
                      <>
                        <div
                          className="absolute inset-y-3 left-0 w-2 cursor-ew-resize rounded-full"
                          onPointerDown={beginDrag({
                            entity: "iteration",
                            mode: "resize-start",
                            id: iteration.id,
                            startDate: iteration.start_date,
                            endDate: iteration.end_date,
                            stopPropagation: true,
                          })}
                        />
                        <div
                          className="absolute inset-y-3 right-0 w-2 cursor-ew-resize rounded-full"
                          onPointerDown={beginDrag({
                            entity: "iteration",
                            mode: "resize-end",
                            id: iteration.id,
                            startDate: iteration.start_date,
                            endDate: iteration.end_date,
                            stopPropagation: true,
                          })}
                        />
                      </>
                    )}
                  </div>
                );
              },
            )}
          </div>
        </div>
      </div>
    );

    const timelineWithChart = (
      <div className="space-y-4">
        <div>{timelineCanvas}</div>
        {renderProductionChart()}
      </div>
    );

    if (!showProjectHeader) {
      return timelineWithChart;
    }

    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Roadmap
              </p>
              <h3 className="text-xl font-semibold text-slate-900">
                Capacites & cadences
              </h3>
              <p className="text-sm text-slate-500">
                Une vue epuree des iterations, sprints et slots.
              </p>
            </div>
            <button
              onClick={onIterationAdd}
              className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:border-blue-300 hover:bg-white"
            >
              <Plus className="h-4 w-4" />
              Nouvelle iteration
            </button>
          </div>
          <div className="mt-4">{timelineWithChart}</div>
        </div>
      </div>
    );
  };

  const renderIterationView = () => {
    if (!activeIteration || !iterationTimeline) return null;
    const normalized = iterationTimeline.normalizedIterations[0];
    const iterationSpecificTargets =
      iterationAttributeTargets?.[activeIteration.id] || [];
    return (
      <div className="space-y-3">
        {renderIterationHeader()}
        <div
          className={`rounded-xl border p-3 shadow-sm ${iterationPanelClass}`}
          ref={iterationCanvasRef}
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">Sprints</p>
            <button
              onClick={() => handleAddSprint(activeIteration)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-blue-400 hover:text-blue-600"
            >
              <Plus className="h-3.5 w-3.5" />
              Ajouter un sprint
            </button>
          </div>
          {iterationSlotsOverlay.length > 0 && (
            <div className="relative mt-1 h-6 rounded-full bg-slate-100">
              {iterationSlotsOverlay.map((slot) => (
                <div
                  key={slot.id || slot.name}
                  className="absolute top-0 h-full rounded-full bg-purple-500/20 text-center text-[10px] font-semibold text-purple-800"
                  style={{
                    left: `${slot.leftPercent}%`,
                    width: `${slot.widthPercent}%`,
                  }}
                >
                  <span className="px-2 leading-6">
                    {slot.name || `Slot ${slot.id}`}
                  </span>
                </div>
              ))}
            </div>
          )}
          {iterationTicks.length > 0 && (
            <div className="relative mt-4 h-8">
              {iterationTicks.map((tick) => (
                <div
                  key={`iter-grid-${tick.key}`}
                  className="pointer-events-none absolute inset-y-0 w-px bg-slate-100"
                  style={{ left: `${tick.position}%` }}
                />
              ))}
              {iterationTicks.map((tick) => (
                <div
                  key={`iter-label-${tick.key}`}
                  className="absolute flex -translate-x-1/2 flex-col items-center text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                  style={{ left: `${tick.position}%` }}
                >
                  <span>{tick.label}</span>
                  <span className="mt-1 h-3 w-px bg-slate-200" />
                </div>
              ))}
            </div>
          )}
          {(() => {
            const sprintLayout = layoutSprintsByOverlap(
              normalized.sprints || [],
            );
            const sprintRowHeight = 58;
            const sprintRowGap = 10;
            const sprintTopOffset = 8;
            const sprintLaneHeight = sprintRowHeight + sprintRowGap;
            const sprintContainerHeight = Math.max(
              108,
              sprintLayout.laneCount * sprintLaneHeight -
                sprintRowGap +
                sprintTopOffset * 2,
            );

            return (
              <div
                className="relative mt-3 overflow-visible rounded-xl bg-slate-50 px-3"
                style={{ height: sprintContainerHeight }}
              >
            {iterationTodayPosition !== null && (
              <div
                className="pointer-events-none absolute inset-y-0 w-px bg-emerald-400/80 z-20"
                style={{ left: `${iterationTodayPosition}%` }}
              >
                <span className="absolute -top-6 -translate-x-1/2 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-emerald-600 shadow">
                  Aujourd'hui
                </span>
                <span className="absolute -bottom-2 -translate-x-1/2 rounded-full bg-emerald-400 p-1" />
              </div>
            )}
            {sprintLayout.items.map((sprint) => (
              <div
                key={sprint.key}
                className="group absolute rounded-xl border border-blue-200 bg-white px-3 py-1.5 text-xs text-blue-900 shadow transition hover:border-blue-400 hover:shadow-md"
                style={{
                  left: `${sprint.leftPercent}%`,
                  width: `${sprint.widthPercent}%`,
                  top: `${sprintTopOffset +
                    sprint.lane * sprintLaneHeight}px`,
                }}
                onClick={(event) =>
                  handleTimelineSprintClick(
                    activeIteration.id,
                    sprint,
                    event.currentTarget,
                  )
                }
                onPointerDown={
                  sprint.id
                    ? beginDrag({
                      entity: "sprint",
                      mode: "move",
                      id: sprint.id,
                      startDate: sprint.start_date,
                      endDate: sprint.end_date,
                      stopPropagation: true,
                      pixelsPerDay: iterationPixelsPerDay,
                    })
                    : undefined
                }
              >
                {onSprintDelete && sprint.id && (
                  <button
                    type="button"
                    onClick={(event) => handleTimelineSprintDelete(event, sprint)}
                    onPointerDown={(event) => event.stopPropagation()}
                    className="absolute -right-2 -top-2 hidden rounded-full border border-rose-200 bg-white p-1 text-rose-500 shadow-sm transition hover:bg-rose-50 group-hover:flex"
                    title="Supprimer ce sprint"
                    aria-label="Supprimer ce sprint"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                <p className="font-semibold text-slate-900">
                  {sprint.description || "Sprint"}
                </p>
                <p className="line-clamp-1 text-[11px] text-blue-700">
                  {sprint.vision?.trim() || "Vision à définir"}
                </p>
                {(() => {
                  const completion = sprintProgressMap.get(sprint.id) ?? null;
                  if (completion === null) return null;
                  const completionPercent = Math.min(
                    100,
                    Math.max(0, Math.round(completion * 100)),
                  );
                  return (
                    <div className="mt-2 h-2 rounded-full bg-slate-100/80">
                      <div
                        className="h-full rounded-full bg-blue-500/70"
                        style={{ width: `${completionPercent}%` }}
                        title={`Progression ${completionPercent}%`}
                      />
                    </div>
                  );
                })()}
                {sprint.id && (
                  <>
                    <div
                      className="absolute inset-y-0 left-0 w-1 cursor-ew-resize rounded-l-2xl"
                      onPointerDown={beginDrag({
                        entity: "sprint",
                        mode: "resize-start",
                        id: sprint.id,
                        startDate: sprint.start_date,
                        endDate: sprint.end_date,
                        stopPropagation: true,
                        pixelsPerDay: iterationPixelsPerDay,
                      })}
                    />
                    <div
                      className="absolute inset-y-0 right-0 w-1 cursor-ew-resize rounded-r-2xl"
                      onPointerDown={beginDrag({
                        entity: "sprint",
                        mode: "resize-end",
                        id: sprint.id,
                        startDate: sprint.start_date,
                        endDate: sprint.end_date,
                        stopPropagation: true,
                        pixelsPerDay: iterationPixelsPerDay,
                      })}
                    />
                  </>
                )}
              </div>
            ))}
            {!sprintLayout.items.length && (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                Aucun sprint. Ajoutez-en un pour planifier cette iteration.
              </div>
            )}
          </div>
            );
          })()}
        </div>
        {renderProductionChart()}
        <IterationForecastPanel
          iteration={activeIteration}
          onInlineUpdate={onIterationInlineUpdate}
        />
        <IterationTargetsCard
          iteration={activeIteration}
          attributes={attributes}
          targets={iterationSpecificTargets}
          onAddTarget={onAddIterationAttributeTarget}
          onUpdateTarget={onUpdateIterationAttributeTarget}
          onDeleteTarget={onDeleteIterationAttributeTarget}
        />
      </div>
    );
  };

  const renderSprintView = () => {
    if (!activeSprint) return null;
    const sprintObjectives = objectivesBySprint.get(activeSprint.id) || [];
    const doneObjectives = sprintObjectives
      .filter((obj) => (obj.status || "").toLowerCase() === "done")
      .sort((a, b) => {
        const dateA = toDate(
          a.done_at || a.updated_at || a.created_at || new Date(),
        );
        const dateB = toDate(
          b.done_at || b.updated_at || b.created_at || new Date(),
        );
        return dateA.getTime() - dateB.getTime();
      });
    const backlogObjectives = sprintObjectives.filter(
      (obj) => (obj.status || "").toLowerCase() !== "done",
    );
    const summary = summarizeObjectives(sprintObjectives);

    return (
      <div className="space-y-4">
        {renderSprintHeader()}
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-slate-900">
              Chronologie des objectifs réalisés
            </p>
            {doneObjectives.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">
                Aucun objectif finalisé pour ce sprint.
              </p>
            ) : (
              <div className="mt-4 space-y-3">
                {doneObjectives.map((objective) => (
                  <div key={objective.id} className="flex items-start gap-3">
                    <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {objective.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        Terminé le{" "}
                        {objective.done_at
                          ? new Date(objective.done_at).toLocaleDateString(
                            "fr-FR",
                          )
                          : "Date inconnue"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-slate-900">
              Backlog sprint
            </p>
            {backlogObjectives.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">
                Tous les objectifs du sprint sont terminés.
              </p>
            ) : (
              <div className="mt-4 space-y-3">
                {backlogObjectives.map((objective) => (
                  <div
                    key={objective.id}
                    className="rounded-xl border border-slate-100 bg-slate-50 p-3"
                  >
                    <p className="text-sm font-semibold text-slate-900">
                      {objective.name}
                    </p>
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      {objective.status}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Résumé sprint</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3 text-xs text-slate-600">
            <div>À faire: {summary.todo}</div>
            <div>En cours: {summary.inProgress}</div>
            <div>Terminés: {summary.done}</div>
          </div>
        </div>
        {renderProductionChart()}
      </div>
    );
  };

  return (
    <div className="space-y-6" ref={rootRef}>
      {renderLevelNavigation()}
      {view.level === "project" && renderProjectView()}
      {view.level === "iteration" && renderIterationView()}
      {view.level === "sprint" && renderSprintView()}
    </div>
  );
};

export default ProjectTimeline;
